import { useEffect, MutableRefObject, RefObject } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    el => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true",
  );
}

/**
 * Focus trap + initial focus + restore on cleanup.
 * `restoreOnCleanup` should be false while the sheet stays open but loses frontmost
 * status to a nested sheet — otherwise focus jumps behind the stack.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  previousFocusRef: MutableRefObject<HTMLElement | null>,
  restoreOnCleanup = true,
): void {
  useEffect(() => {
    if (!active) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (!previousFocusRef.current) {
      previousFocusRef.current = (document.activeElement as HTMLElement) || null;
    }

    const focusables = getFocusableElements(container);
    const initial = focusables[0] || container;
    initial.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Tab" || !containerRef.current) {
        return;
      }

      const elements = getFocusableElements(containerRef.current);
      if (elements.length === 0) {
        event.preventDefault();
        containerRef.current.focus();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      const activeEl = document.activeElement as HTMLElement;

      if (event.shiftKey) {
        if (activeEl === first || !containerRef.current.contains(activeEl)) {
          event.preventDefault();
          last.focus();
        }
      } else if (activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (!restoreOnCleanup) {
        return;
      }
      const previous = previousFocusRef.current;
      previousFocusRef.current = null;
      if (previous && typeof previous.focus === "function") {
        previous.focus();
      }
    };
  }, [active, containerRef, previousFocusRef, restoreOnCleanup]);
}
